/**
 * panellock: keep a side panel shut for a while.
 *
 * Founder, 19 Oct 2026: "whenever we reload the sidebars open full, sometimes
 * we dont want the sidebars to open ... lock the sidebar opening for some time
 * ... from 5 minutes to 3 hours ... auto removes after the time has expired or
 * could be removed manually". And Act Now can never be shut for good: "u can
 * miss something important and this is for ur good".
 *
 * - A lock is an end time on this device (localStorage), nothing on the
 *   server: no call. Another tab hears it through the `storage` event.
 * - While a panel is locked it stays shut through reloads, client switches and
 *   page changes. Opening it by hand (its handle, `[`, `ss`) ends the lock.
 * - 3 hours is the most, for both panels.
 *
 * This file is in the shell's first load (the sidebar must know before it
 * draws), so it holds the lock and nothing else: the clock's words are in
 * `lockWords.ts`, the clock in `PanelLockButton.tsx` and `ss` in
 * `panelKeys.ts`, all lazy (`npm run check:bundle`, which main sat 0.1 KB
 * under on 19 Oct 2026).
 */
import { useEffect, useState } from 'react';

export type LockPanel = 'menu' | 'actnow';

/** The most a lock can be, in minutes (the choices are in `lockWords.ts`). */
export const MAX_LOCK_MIN = 180;

const LOCK_EVENT = 'karr:panel-lock';
const key = (p: LockPanel) => `karr-panel-lock:${p}`;

function write(p: LockPanel, until: number | null): void {
    try {
        if (until) localStorage.setItem(key(p), String(until));
        else localStorage.removeItem(key(p));
    } catch { /* private window: no lock */ }
    window.dispatchEvent(new Event(LOCK_EVENT));
}

/** The end of the lock (epoch ms), or null when the panel is free. */
export function lockedUntil(p: LockPanel, now = Date.now()): number | null {
    let until = NaN;
    try { until = Number(localStorage.getItem(key(p)) || NaN); } catch { /* blocked: free */ }
    // Lapsed, or further out than the cap (not written by us): free.
    return until > now && until - now <= (MAX_LOCK_MIN + 1) * 60_000 ? until : null;
}

export function lockPanel(p: LockPanel, minutes: number): void {
    write(p, Date.now() + Math.max(1, Math.min(MAX_LOCK_MIN, Math.round(minutes))) * 60_000);
}

export function unlockPanel(p: LockPanel): void {
    write(p, null);
}

/**
 * The panel's lock, kept current: it re-reads on a lock or unlock anywhere
 * (this tab or another) and frees itself when the time is up.
 */
export function usePanelLock(p: LockPanel) {
    const [until, setUntil] = useState(() => lockedUntil(p));
    useEffect(() => {
        const read = () => setUntil(lockedUntil(p));
        window.addEventListener(LOCK_EVENT, read);
        window.addEventListener('storage', read);
        const end = until ? window.setTimeout(read, until - Date.now() + 50) : 0;
        return () => {
            window.removeEventListener(LOCK_EVENT, read);
            window.removeEventListener('storage', read);
            window.clearTimeout(end);
        };
    }, [p, until]);
    return { until, lock: (minutes: number) => lockPanel(p, minutes), unlock: () => unlockPanel(p) };
}
